import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft } from 'lucide-react';
import {
  ENTERPRISE_COMMAND_DESTINATIONS,
  type CommandDestination,
} from '../../config/enterpriseCommandDestinations';

function matches(query: string, item: CommandDestination): boolean {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  const hay = `${item.label} ${item.group} ${item.keywords ?? ''} ${item.to}`.toLowerCase();
  return hay.includes(q);
}

interface EnterpriseCommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const EnterpriseCommandPalette: React.FC<EnterpriseCommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const filtered = useMemo(
    () => ENTERPRISE_COMMAND_DESTINATIONS.filter((d) => matches(query, d)),
    [query]
  );

  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const go = useCallback(
    (item: CommandDestination) => {
      navigate(item.to);
      onClose();
    },
    [navigate, onClose]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      }
      if (e.key === 'Enter' && filtered[highlight]) {
        e.preventDefault();
        go(filtered[highlight]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, filtered, highlight, go, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-label="Close command palette"
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-xl border border-enterprise-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="flex items-center gap-2 border-b border-enterprise-100 px-3 py-2">
          <Search className="h-4 w-4 shrink-0 text-enterprise-400" />
          <input
            autoFocus
            className="min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-enterprise-900 outline-none placeholder:text-enterprise-400"
            placeholder="Jump to page…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd className="hidden rounded border border-enterprise-200 bg-enterprise-50 px-1.5 py-0.5 text-[10px] text-enterprise-500 sm:inline">
            esc
          </kbd>
        </div>
        <ul className="max-h-[min(50vh,320px)] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-enterprise-500">No matches</li>
          ) : (
            filtered.map((item, idx) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm ${
                    idx === highlight ? 'bg-emerald-50 text-emerald-900' : 'text-enterprise-800 hover:bg-enterprise-50'
                  }`}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => go(item)}
                >
                  <span>
                    <span className="font-medium">{item.label}</span>
                    <span className="ml-2 text-xs text-enterprise-400">{item.group}</span>
                  </span>
                  {idx === highlight && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-enterprise-400" />}
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="border-t border-enterprise-100 px-3 py-2 text-[10px] text-enterprise-500">
          <span className="font-medium text-enterprise-600">⌘K</span> / <span className="font-medium text-enterprise-600">Ctrl+K</span> to toggle
          · ↑↓ navigate · ↵ open
        </div>
      </div>
    </div>
  );
};
